import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker, { inlineSelectStyle } from '../components/SchoolPicker';
import apiService from '../services/api';
import AnalyticsMetricRows, { RANGE_OPTIONS, VIEWER_TIMEZONE } from '../components/AnalyticsMetricRows';
import '../styles/StaffPages.css';

const formatDateTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// "Jan 01 2027 - 5:30pm". Built by hand rather than via toLocaleString because the
// lowercase am/pm and the zero-padded day aren't reachable through Intl options.
const formatRedemptionDateTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const rawHours = d.getHours();
  const suffix = rawHours >= 12 ? 'pm' : 'am';
  const hours = rawHours % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${SHORT_MONTHS[d.getMonth()]} ${day} ${d.getFullYear()} - ${hours}:${minutes}${suffix}`;
};

const formatPayloadFull = payload => {
  try {
    return JSON.stringify(payload, null, 2);
  } catch (_) {
    return '—';
  }
};

const buildMockLoginsTimeseries = granularity => {
  const now = new Date();
  if (granularity === 'year') {
    const out = [];
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const base = 250 + (11 - i) * 18;
      const wave = Math.round(60 * Math.sin((11 - i) * 0.9));
      out.push({ bucket: d.toISOString(), count: Math.max(40, base + wave) });
    }
    return out;
  }
  if (granularity === 'month') {
    const out = [];
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    let cursor = new Date(monthStart);
    cursor.setHours(0, 0, 0, 0);
    while (cursor <= monthEnd) {
      const day = cursor.getDate();
      const weekIndex = Math.floor((day - 1) / 7);
      const base = 520 + weekIndex * 80;
      const wave = Math.round(35 * Math.sin((weekIndex + 1) * 0.9));
      out.push({ bucket: new Date(cursor).toISOString(), count: Math.max(120, base + wave) });
      cursor.setDate(cursor.getDate() + 7);
    }
    return out;
  }
  if (granularity === 'week') {
    const out = [];
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const weekday = d.getDay();
      const workdayBoost = weekday >= 1 && weekday <= 5 ? 45 : 10;
      const wave = Math.round(12 * Math.sin(i * 0.8));
      out.push({ bucket: d.toISOString(), count: Math.max(30, 110 + workdayBoost + wave) });
    }
    return out;
  }
  const out = [];
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  for (let i = 0; i < 24; i += 1) {
    const d = new Date(start);
    d.setHours(i, 0, 0, 0);
    const businessPeak = i >= 9 && i <= 19 ? 35 : 10;
    const wave = Math.round(18 * Math.sin((i / 24) * Math.PI * 2));
    const jitter = ((i * 11) % 9) - 4;
    out.push({ bucket: d.toISOString(), count: Math.max(4, 28 + businessPeak + wave + jitter) });
  }
  return out;
};

const getRewardImageUrl = imagePath => {
  if (!imagePath) return null;
  if (imagePath.startsWith('s3://')) {
    return imagePath.replace('s3://', 'https://');
  }
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  if (!imagePath.includes('/')) {
    return null;
  }
  return imagePath;
};

const REWARD_THUMB_SIZE = 56;

// The row already fell back when there was no URL at all, but a present-and-broken
// URL (dead S3 object, 404) rendered as the browser's torn-image glyph. onError is
// the only way to catch that, and it needs state, hence a component.
function RewardThumb({ imagePath }) {
  const src = getRewardImageUrl(imagePath);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [imagePath]);

  const base = {
    width: REWARD_THUMB_SIZE,
    height: REWARD_THUMB_SIZE,
    borderRadius: 8,
    display: 'block',
    flexShrink: 0,
  };

  if (!src || failed) {
    return (
      <div
        style={{
          ...base,
          background: '#e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4.5" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="8.75" cy="9.75" r="1.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="m4.5 17.5 4-4 3 3 3-2.5 4.5 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      style={{ ...base, objectFit: 'cover', background: '#f0f0f0' }}
    />
  );
}

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
};

const thTd = {
  borderBottom: '1px solid #e9ecef',
  padding: '10px 8px',
  textAlign: 'left',
  verticalAlign: 'top',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const REWARDS_PAGE_SIZE_OPTIONS = [20, 50, 100];

const fillerTd = {
  ...thTd,
  color: '#b5b5b5',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

function analyticTableSkeletonMinHeight(rowsPerPage) {
  const theadH = 48;
  const rowH = 52;
  return theadH + Math.max(rowsPerPage, 1) * rowH;
}

function eventFillerRows(count) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={`event-ph-${i}`} aria-hidden="true">
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={{ ...fillerTd, fontFamily: 'ui-monospace, monospace' }}>—</td>
    </tr>
  ));
}

function loginFillerRows(count) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={`login-ph-${i}`} aria-hidden="true">
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={fillerTd}>—</td>
      <td style={{ ...fillerTd, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>—</td>
      <td style={fillerTd}>—</td>
    </tr>
  ));
}

const modalOverlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '24px 16px',
  overflow: 'auto',
};

const modalBox = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 960,
  width: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  marginTop: 24,
  marginBottom: 48,
};

const SuperuserAnalytics = ({ variant = 'superuser' } = {}) => {
  const isStaffScope = variant === 'staff';
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [days, setDays] = useState(7);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  // Analytics is embed-only: the surface is fixed rather than chosen, so these are
  // constants that still flow into the same request params and effect deps.
  const clientSurface = 'embed';
  const loginsClientSurface = 'embed';
  const [events, setEvents] = useState([]);
  const [eventsCount, setEventsCount] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [logins, setLogins] = useState([]);
  const [loginsCount, setLoginsCount] = useState(0);
  const [loginsPage, setLoginsPage] = useState(1);
  const [loginsTotalPages, setLoginsTotalPages] = useState(1);
  const [loginsLoading, setLoginsLoading] = useState(true);
  const [eventsPageSize, setEventsPageSize] = useState(20);
  const [loginsPageSize, setLoginsPageSize] = useState(20);

  const [rewardsScope, setRewardsScope] = useState('all');
  const [rewardsSchoolId, setRewardsSchoolId] = useState('');
  const [rewardsSchools, setRewardsSchools] = useState([]);
  const [rewardsRows, setRewardsRows] = useState([]);
  const [rewardsPage, setRewardsPage] = useState(1);
  const [rewardsPageSize, setRewardsPageSize] = useState(20);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [rewardsError, setRewardsError] = useState('');
  const [rewardModalId, setRewardModalId] = useState(null);
  const [rewardDetail, setRewardDetail] = useState(null);
  const [rewardDetailLoading, setRewardDetailLoading] = useState(false);
  const [rewardViewersPage, setRewardViewersPage] = useState(1);
  const [rewardPurchasesPage, setRewardPurchasesPage] = useState(1);
  const usersByClientChartRef = useRef(null);
  const loginsTrendChartRef = useRef(null);
  const [loginsGranularity, setLoginsGranularity] = useState('day');
  const [useMockLoginsTrend] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (isStaffScope) {
          if (!currentUser?.is_staff) {
            navigate('/login/');
            return;
          }
        } else if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        setAuthChecked(true);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login/');
      }
    };
    checkAuth();
  }, [navigate, isStaffScope]);

  // The staff page renders only the metric rows and the rewards list, so the
  // overview / events / logins requests below have no consumer there — skip them
  // rather than pay for three unused round trips on every period change.
  const fetchOverview = useCallback(async () => {
    if (isStaffScope) {
      setOverviewLoading(false);
      return;
    }
    setOverviewLoading(true);
    try {
      const params = { days };
      if (clientSurface) {
        params.client_surface = clientSurface;
      }
      if (loginsClientSurface) {
        params.logins_client_surface = loginsClientSurface;
      }
      params.logins_granularity = loginsGranularity;
      params.tz = VIEWER_TIMEZONE;
      const data = isStaffScope
        ? await apiService.getStaffAnalyticsOverview(params)
        : await apiService.getSuperuserAnalyticsOverview(params);
      setOverview(data);
    } catch (error) {
      console.error('Failed to load analytics overview:', error);
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [days, clientSurface, loginsClientSurface, loginsGranularity, isStaffScope]);

  const fetchEvents = useCallback(async () => {
    if (isStaffScope) {
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    try {
      const params = { days, page: eventsPage, page_size: eventsPageSize };
      if (eventTypeFilter) {
        params.event_type = eventTypeFilter;
      }
      if (clientSurface) {
        params.client_surface = clientSurface;
      }
      const data = isStaffScope
        ? await apiService.getStaffAnalyticsEvents(params)
        : await apiService.getSuperuserAnalyticsEvents(params);
      setEvents(data.results || []);
      setEventsCount(data.count || 0);
      setEventsTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Failed to load events:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [days, eventsPage, eventTypeFilter, clientSurface, eventsPageSize, isStaffScope]);

  const fetchLogins = useCallback(async () => {
    if (isStaffScope) {
      setLoginsLoading(false);
      return;
    }
    setLoginsLoading(true);
    try {
      const params = {
        days,
        page: loginsPage,
        page_size: loginsPageSize,
      };
      if (loginsClientSurface) {
        params.client_surface = loginsClientSurface;
      }
      const data = isStaffScope
        ? await apiService.getStaffAnalyticsLogins(params)
        : await apiService.getSuperuserAnalyticsLogins(params);
      setLogins(data.results || []);
      setLoginsCount(data.count || 0);
      setLoginsTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Failed to load logins:', error);
      setLogins([]);
    } finally {
      setLoginsLoading(false);
    }
  }, [days, loginsPage, loginsClientSurface, loginsPageSize, isStaffScope]);

  const fetchRewardsList = useCallback(async () => {
    if (!authChecked) return;
    setRewardsLoading(true);
    setRewardsError('');
    try {
      const params = { days };
      if (!isStaffScope) {
        params.scope = rewardsScope;
        if (rewardsScope === 'school') {
          if (!rewardsSchoolId) {
            setRewardsRows([]);
            setRewardsPage(1);
            setRewardsError('Please select a school');
            return;
          }
          params.school_id = rewardsSchoolId;
        }
      }
      const data = isStaffScope
        ? await apiService.getStaffRewardAnalytics(params)
        : await apiService.getSuperuserRewardAnalytics(params);
      setRewardsRows(data.results || []);
      setRewardsPage(1);
    } catch (error) {
      console.error(error);
      setRewardsRows([]);
      setRewardsPage(1);
      setRewardsError('Failed to load rewards list.');
    } finally {
      setRewardsLoading(false);
    }
  }, [authChecked, days, rewardsScope, rewardsSchoolId, isStaffScope]);

  useEffect(() => {
    const loadSchools = async () => {
      if (!authChecked || isStaffScope) return;
      try {
        const data = await apiService.getSchools();
        const list = data?.results ?? data ?? [];
        setRewardsSchools(Array.isArray(list) ? list : []);
      } catch (_) {
        setRewardsSchools([]);
      }
    };
    loadSchools();
  }, [authChecked, isStaffScope]);

  useEffect(() => {
    if (!authChecked) return;
    fetchRewardsList();
  }, [authChecked, fetchRewardsList]);

  useEffect(() => {
    if (rewardsScope === 'all') {
      setRewardsSchoolId('');
    }
  }, [rewardsScope]);

  useEffect(() => {
    setRewardsPage(1);
  }, [rewardsPageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rewardsRows.length / rewardsPageSize) || 1);
    setRewardsPage(p => (p > totalPages ? totalPages : p));
  }, [rewardsRows.length, rewardsPageSize]);

  useEffect(() => {
    setRewardViewersPage(1);
    setRewardPurchasesPage(1);
  }, [rewardModalId, days]);

  const fetchRewardDetail = useCallback(async () => {
    if (!rewardModalId) return;
    setRewardDetailLoading(true);
    try {
      const detailParams = {
        days,
        viewers_page: rewardViewersPage,
        purchases_page: rewardPurchasesPage,
        viewers_page_size: 25,
        purchases_page_size: 25,
      };
      const data = isStaffScope
        ? await apiService.getStaffRewardAnalyticsDetail(rewardModalId, detailParams)
        : await apiService.getSuperuserRewardAnalyticsDetail(rewardModalId, detailParams);
      setRewardDetail(data);
    } catch (error) {
      console.error(error);
      setRewardDetail(null);
    } finally {
      setRewardDetailLoading(false);
    }
  }, [rewardModalId, days, rewardViewersPage, rewardPurchasesPage, isStaffScope]);

  useEffect(() => {
    if (!rewardModalId) return;
    fetchRewardDetail();
  }, [rewardModalId, fetchRewardDetail]);

  const closeRewardModal = () => {
    setRewardModalId(null);
    setRewardDetail(null);
    setRewardViewersPage(1);
    setRewardPurchasesPage(1);
  };

  useEffect(() => {
    if (!authChecked) return;
    fetchOverview();
  }, [authChecked, fetchOverview]);

  useEffect(() => {
    if (!authChecked) return;
    fetchEvents();
  }, [authChecked, fetchEvents]);

  useEffect(() => {
    if (!authChecked) return;
    fetchLogins();
  }, [authChecked, fetchLogins]);

  useEffect(() => {
    setEventsPage(1);
  }, [eventsPageSize]);

  useEffect(() => {
    setLoginsPage(1);
  }, [loginsPageSize]);

  useEffect(() => {
    setEventsPage(1);
  }, [eventTypeFilter]);

  useEffect(() => {
    if (!usersByClientChartRef.current) return;
    d3.select(usersByClientChartRef.current).selectAll('*').remove();

    const appUsers = Number(overview?.login_users_by_surface?.app ?? 0);
    const embedUsers = Number(overview?.login_users_by_surface?.embed ?? 0);
    const data = [
      { id: 'app', name: 'App', value: appUsers },
      { id: 'embed', name: 'Embed', value: embedUsers },
    ];

    if (!data.some(d => d.value > 0)) {
      d3.select(usersByClientChartRef.current)
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'center')
        .style('height', '260px')
        .style('width', '100%')
        .style('color', '#9CA3AF')
        .style('font-family', 'Baikal Book')
        .style('font-size', '14px')
        .text('No users data for selected period');
      return;
    }

    const margin = { top: 20, right: 30, bottom: 70, left: 60 };
    const width = 420 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;

    const svg = d3
      .select(usersByClientChartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(data.map(d => d.id))
      .range([0, width])
      .padding(0.3);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, d => d.value) * 1.1 || 10])
      .nice()
      .range([height, 0]);

    svg
      .append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `${d}`).tickSize(0).tickPadding(10))
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', '#000000')
      .style('font-weight', '500');

    svg.select('.domain').style('stroke', 'none');

    const bars = svg.selectAll('.bar').data(data).enter().append('g').attr('class', 'bar-group');

    bars
      .append('rect')
      .attr('class', 'bar-base')
      .attr('x', d => x(d.id))
      .attr('y', d => y(d.value))
      .attr('width', x.bandwidth())
      .attr('height', d => height - y(d.value))
      .attr('fill', '#F8F8F8');

    bars.each(function (d) {
      const barHeight = height - y(d.value);
      const lineSpacing = 5;
      const lineHeight = 2.5;
      const numLines = Math.floor(barHeight / lineSpacing);
      const barX = x(d.id);
      const barWidth = x.bandwidth();
      const centerX = barX + barWidth / 2;
      const barGroup = d3.select(this);
      for (let i = 0; i < numLines; i += 1) {
        const lineY = height - (i * lineSpacing + lineHeight);
        if (lineY > y(d.value)) {
          const lineWidth = barWidth - 4;
          barGroup
            .append('rect')
            .attr('class', 'horizontal-line')
            .attr('x', centerX - lineWidth / 2)
            .attr('y', lineY)
            .attr('width', lineWidth)
            .attr('height', lineHeight)
            .attr('fill', '#0000000F')
            .attr('rx', 2)
            .attr('ry', 2);
        }
      }
    });

    bars
      .append('rect')
      .attr('class', 'bar-top')
      .attr('x', d => {
        const centerX = x(d.id) + x.bandwidth() / 2;
        const barWidth = x.bandwidth() - 4;
        return centerX - barWidth / 2;
      })
      .attr('y', d => y(d.value) - 1)
      .attr('width', x.bandwidth() - 4)
      .attr('height', 2)
      .attr('fill', '#000')
      .attr('rx', 2)
      .attr('ry', 2);

    bars
      .append('text')
      .attr('x', d => x(d.id) + x.bandwidth() / 2)
      .attr('y', height + 26)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-family', 'Baikal Book')
      .style('font-weight', '700')
      .style('fill', '#111827')
      .text(d => d.name.toUpperCase());
  }, [overview]);

  useEffect(() => {
    if (!loginsTrendChartRef.current) return;
    d3.select(loginsTrendChartRef.current).selectAll('*').remove();
    d3.selectAll('.d3-tooltip-logins').remove();

    const raw = useMockLoginsTrend
      ? buildMockLoginsTimeseries(loginsGranularity)
      : Array.isArray(overview?.logins_timeseries)
        ? overview.logins_timeseries
        : [];
    const data = raw
      .map(row => ({
        date: row?.bucket ? new Date(row.bucket) : null,
        count: Number(row?.count ?? 0),
      }))
      .filter(row => row.date && !Number.isNaN(row.date.getTime()))
      .sort((a, b) => a.date - b.date);

    if (!data.length) {
      d3.select(loginsTrendChartRef.current)
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'center')
        .style('height', '260px')
        .style('width', '100%')
        .style('color', '#9CA3AF')
        .style('font-family', 'Baikal Book')
        .style('font-size', '14px')
        .text('No login trend data for selected period');
      return;
    }

    const margin = { top: 10, right: 20, bottom: 30, left: 40 };
    const width = 600 - margin.left - margin.right;
    const height = 260 - margin.top - margin.bottom;

    const svg = d3
      .select(loginsTrendChartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Local scale for every granularity. The backend now buckets in the viewer's
    // timezone and emits offsets to match, so a UTC scale here would re-render those
    // instants in UTC and shift every hourly label by the viewer's offset.
    const x = d3
      .scaleTime()
      .domain(d3.extent(data, d => d.date))
      .range([0, width]);

    const y = d3
      .scaleLinear()
      .domain([0, (d3.max(data, d => d.count) || 0) * 1.2 || 10])
      .nice()
      .range([height, 0]);

    const grid = d3.axisBottom(x).tickSize(-height).tickFormat('');
    if (loginsGranularity === 'day') grid.ticks(d3.timeHour.every(2));
    if (loginsGranularity === 'week') grid.ticks(d3.timeDay.every(1));
    if (loginsGranularity === 'month') grid.ticks(d3.timeWeek.every(1));
    if (loginsGranularity === 'year') grid.ticks(d3.timeMonth.every(1));

    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(grid)
      .style('color', '#E5E7EB')
      .style('stroke-dasharray', '3,3');

    let xTickFormat = d3.timeFormat('%b %d');
    if (loginsGranularity === 'week') xTickFormat = d3.timeFormat('%a');
    if (loginsGranularity === 'month') xTickFormat = d3.timeFormat('%b %d');
    if (loginsGranularity === 'year') xTickFormat = d3.timeFormat('%b');
    if (loginsGranularity === 'day') xTickFormat = d3.timeFormat('%H:%M');

    const xAxis = d3.axisBottom(x).tickFormat(xTickFormat);
    if (loginsGranularity === 'day') xAxis.ticks(d3.timeHour.every(2));
    if (loginsGranularity === 'week') xAxis.ticks(d3.timeDay.every(1));
    if (loginsGranularity === 'month') xAxis.ticks(d3.timeWeek.every(1));
    if (loginsGranularity === 'year') xAxis.ticks(d3.timeMonth.every(1));

    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', '#000000');

    svg
      .append('g')
      .call(d3.axisLeft(y).ticks(5).tickSize(0).tickPadding(10))
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', '#000000');

    svg.selectAll('.domain').style('stroke', 'none');

    const line = d3
      .line()
      .x(d => x(d.date))
      .y(d => y(d.count))
      .curve(d3.curveMonotoneX);

    svg
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#00C8FF')
      .attr('stroke-width', 4)
      .attr('d', line);

    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'd3-tooltip-logins')
      .style('position', 'absolute')
      .style('padding', '8px 12px')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('font-family', 'Baikal Book')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    const focus = svg
      .append('circle')
      .attr('r', 5)
      .attr('fill', '#00C8FF')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('opacity', 0);

    const bisectDate = d3.bisector(d => d.date).left;

    let tooltipDateFormat = d3.timeFormat('%b %d, %Y');
    if (loginsGranularity === 'day') tooltipDateFormat = d3.timeFormat('%H:%M');
    if (loginsGranularity === 'week') tooltipDateFormat = d3.timeFormat('%A, %b %d');
    if (loginsGranularity === 'month') tooltipDateFormat = d3.timeFormat('%b %d, %Y');
    if (loginsGranularity === 'year') tooltipDateFormat = d3.timeFormat('%b %Y');

    svg
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .style('pointer-events', 'all')
      .on('mousemove', event => {
        const [mouseX] = d3.pointer(event);
        const xDate = x.invert(mouseX);
        const i = bisectDate(data, xDate, 1);
        const left = data[Math.max(0, i - 1)];
        const right = data[Math.min(data.length - 1, i)];
        const d =
          right && left
            ? xDate - left.date > right.date - xDate
              ? right
              : left
            : left || right;
        if (!d) return;

        focus.attr('cx', x(d.date)).attr('cy', y(d.count)).style('opacity', 1);

        tooltip
          .html(
            `<strong>${tooltipDateFormat(d.date)}</strong><br/>Logins: ${d.count.toLocaleString('en-US')}`,
          )
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY - 28}px`)
          .transition()
          .duration(80)
          .style('opacity', 1);
      })
      .on('mouseleave', () => {
        focus.style('opacity', 0);
        tooltip.transition().duration(150).style('opacity', 0);
      });

    return () => {
      tooltip.remove();
    };
  }, [overview, loginsGranularity, useMockLoginsTrend]);

  if (!authChecked) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection={isStaffScope ? 'analytics' : 'superuser'} />
        <div className="admin-main">
          <div className="admin-content">
            <p className="staff-page-content">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  const eb = overview?.product_events_by_type || {};
  const lm = overview?.logins_by_method || {};

  // Same wording as the page's period selector, so the modal can't describe the
  // window differently from the control that set it.
  const periodLabel = RANGE_OPTIONS.find(opt => opt.value === days)?.label || `${days} days`;

  const rewardsTotal = rewardsRows.length;
  const rewardsTableTotalPages = Math.max(1, Math.ceil(rewardsTotal / rewardsPageSize) || 1);
  // Staff see the rewards table as a plain list — no per-page control and no
  // pager — so paging it would strand rows behind a control that isn't rendered.
  const rewardsPagedRows = isStaffScope
    ? rewardsRows
    : rewardsRows.slice((rewardsPage - 1) * rewardsPageSize, rewardsPage * rewardsPageSize);

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection={isStaffScope ? 'analytics' : 'superuser'} />
      <div className="admin-main">
        <div className="admin-content">
          {!isStaffScope ? (
            <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          ) : null}
          <div className="staff-page-header-row" style={{ marginTop: '24px' }}>
            <div>
              <h1 className="staff-page-header">Analytics</h1>
            </div>
            <div
              className="staff-page-subheader"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Period
                <select
                  value={days}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setDays(v);
                    setEventsPage(1);
                    setLoginsPage(1);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #e9ecef',
                    fontFamily: 'inherit',
                  }}
                >
                  {RANGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {isStaffScope ? <AnalyticsMetricRows days={days} /> : null}

          {!isStaffScope ? (
          <div className="add-reward-section" style={{ marginTop: 24 }}>
            <h2 className="staff-page-header" style={{ fontSize: '22px', marginBottom: '12px' }}>
              Summary
            </h2>
            {overviewLoading ? (
              <p className="staff-page-content">Loading summary…</p>
            ) : overview ? (
              <>
                <p className="staff-page-content" style={{ marginBottom: 16 }}>
                  Split (whole period){' '}
                  <strong>events</strong>: app {overview.product_events_totals_by_surface?.app ?? 0}, embed{' '}
                  {overview.product_events_totals_by_surface?.embed ?? 0}
                  {' · '}
                  <strong>logins</strong>: app {overview.logins_totals_by_surface?.app ?? 0}, embed{' '}
                  {overview.logins_totals_by_surface?.embed ?? 0}
                  {clientSurface ? (
                    <span style={{ opacity: 0.75 }}>
                      {' '}
                      — product events (summary totals and feed below) filtered to{' '}
                      <strong>{clientSurface}</strong>
                    </span>
                  ) : null}
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: '24px',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#F8F8F8',
                      borderRadius: '16px',
                      padding: '18px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      flex: '1 1 400px',
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <h4
                        style={{
                          fontFamily: 'Baikal Book',
                          fontSize: '16px',
                          marginBottom: '4px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: 0,
                        }}
                      >
                        Total Users by Client
                      </h4>
                      <p
                        style={{
                          fontFamily: 'Baikal Book',
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: 0,
                        }}
                      >
                        Unique users with successful logins in selected period
                      </p>
                    </div>
                    <div ref={usersByClientChartRef} />
                  </div>

                  <div
                    style={{
                      backgroundColor: '#F8F8F8',
                      borderRadius: '16px',
                      padding: '18px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      flex: '1 1 600px',
                    }}
                  >
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <h4
                          style={{
                            fontFamily: 'Baikal Book',
                            fontSize: '16px',
                            marginBottom: '4px',
                            fontWeight: '600',
                            color: '#111827',
                            margin: 0,
                          }}
                        >
                          Logins over selected period
                        </h4>
                        <p
                          style={{
                            fontFamily: 'Baikal Book',
                            fontSize: '12px',
                            color: '#6B7280',
                            margin: 0,
                          }}
                        >
                          Trend of successful logins
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: 'Baikal Book', fontSize: 12, color: '#6B7280' }}>Group by</span>
                        <select
                          value={loginsGranularity}
                          onChange={e => setLoginsGranularity(e.target.value)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #e9ecef',
                            fontFamily: 'inherit',
                            minWidth: 110,
                          }}
                        >
                          <option value="year">Year</option>
                          <option value="month">Month</option>
                          <option value="week">Week</option>
                          <option value="day">Day</option>
                        </select>
                      </div>
                    </div>
                    <div ref={loginsTrendChartRef} />
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 16,
                  }}
                >
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Product events
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800 }}>{overview.product_events_total}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Logins recorded
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800 }}>{overview.logins_total}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Screen visits
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{eb.screen_visit ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Searches
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{eb.search ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Offer detail views
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{eb.offer_detail_view ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Offer link clicks
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{eb.offer_link_click ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Reward detail views
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{eb.reward_detail_view ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Reward clicks
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{eb.reward_click ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Login email
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{lm.email ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Login Google
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{lm.google ?? 0}</div>
                  </div>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
                    <div className="staff-page-subheader" style={{ opacity: 0.7 }}>
                      Login Apple
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{lm.apple ?? 0}</div>
                  </div>
                </div>
              </>
            ) : (
              <p className="staff-page-content">Could not load summary.</p>
            )}
          </div>
          ) : null}

          {!isStaffScope && (overview?.top_screens?.length || overview?.top_search_queries?.length) ? (
            <div style={{ marginTop: 24, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {overview.top_screens?.length ? (
                <div className="add-reward-section" style={{ flex: '1 1 320px' }}>
                  <h3 className="staff-section-header">Top screens</h3>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thTd}>Screen</th>
                        <th style={{ ...thTd, width: 80 }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.top_screens.map((row, idx) => (
                        <tr key={`${row.screen}-${idx}`}>
                          <td style={thTd}>{row.screen}</td>
                          <td style={thTd}>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {overview.top_search_queries?.length ? (
                <div className="add-reward-section" style={{ flex: '1 1 320px' }}>
                  <h3 className="staff-section-header">Top search terms</h3>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thTd}>Query</th>
                        <th style={{ ...thTd, width: 80 }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.top_search_queries.map((row, idx) => (
                        <tr key={`${row.query}-${idx}`}>
                          <td style={thTd}>{row.query}</td>
                          <td style={thTd}>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="add-reward-section" style={{ marginTop: 24 }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div>
                <h2 className="staff-page-header" style={{ fontSize: '22px', marginBottom: '8px' }}>
                  Rewards
                </h2>
                {!isStaffScope ? (
                  <p className="staff-page-subheader" style={{ opacity: 0.7, marginBottom: 0 }}>
                    Viewed counts = <code style={{ fontSize: 'inherit' }}>reward_detail_view</code> events; purchased =
                    redemptions (excl. cancelled).
                  </p>
                ) : null}
                {!isStaffScope && !rewardsLoading && rewardsRows.length > 0 ? (
                  <p className="staff-page-subheader" style={{ opacity: 0.7, marginTop: 8, marginBottom: 0 }}>
                    {rewardsTotal} rewards · page {rewardsPage} of {rewardsTableTotalPages} · {rewardsPageSize} per page
                  </p>
                ) : null}
              </div>
              <div
                style={{
                  display: isStaffScope ? 'none' : 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                {!isStaffScope ? (
                  <label className="staff-page-subheader" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Catalog
                    <select
                      value={rewardsScope}
                      onChange={e => setRewardsScope(e.target.value)}
                      style={{ ...inlineSelectStyle('md'), minWidth: 120 }}
                    >
                      <option value="all">All</option>
                      <option value="school">School</option>
                    </select>
                  </label>
                ) : null}
                {!isStaffScope && rewardsScope === 'school' ? (
                  <label
                    className="staff-page-subheader"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 1 }}
                  >
                    School
                    <SchoolPicker
                      schools={rewardsSchools}
                      value={rewardsSchools.find(s => s.id === rewardsSchoolId) || null}
                      onChange={school => setRewardsSchoolId(school?.id || '')}
                      size="md"
                    />
                  </label>
                ) : null}
                <label
                  className="staff-page-subheader"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 1 }}
                >
                  Per page
                  <select
                    value={rewardsPageSize}
                    onChange={e => setRewardsPageSize(Number(e.target.value))}
                    style={{ ...inlineSelectStyle('md'), minWidth: 72 }}
                  >
                    {REWARDS_PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            {rewardsError ? (
              <p className="staff-page-content" style={{ marginTop: 16, color: '#c62828' }}>
                {rewardsError}
              </p>
            ) : null}
            <div style={{ marginTop: 16 }}>
              {rewardsLoading ? (
                <p className="staff-page-content">Loading rewards…</p>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={{ ...thTd, width: 76 }}>Image</th>
                          <th style={thTd}>Reward</th>
                          {!isStaffScope ? <th style={thTd}>School</th> : null}
                          <th style={thTd}>Points</th>
                          <th style={{ ...thTd, width: 100 }}>Viewed</th>
                          <th style={{ ...thTd, width: 100 }}>Redeemed</th>
                          {!isStaffScope ? <th style={{ ...thTd, width: 80 }}>Active</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {rewardsPagedRows.map(row => (
                          <tr
                            key={row.id}
                            onClick={() => setRewardModalId(row.id)}
                            style={{ cursor: 'pointer' }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setRewardModalId(row.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <td style={{ ...thTd, verticalAlign: 'middle' }}>
                              <RewardThumb imagePath={row.primary_image_url} />
                            </td>
                            <td style={thTd}>
                              <span style={{ fontWeight: 600, color: '#1976d2' }}>{row.title}</span>
                              {row.merchant_name ? (
                                <div className="staff-page-subheader" style={{ opacity: 0.75, marginTop: 4 }}>
                                  {row.merchant_name}
                                </div>
                              ) : null}
                            </td>
                            {!isStaffScope ? <td style={thTd}>{row.school_name || '—'}</td> : null}
                            <td style={thTd}>{row.points_cost}</td>
                            <td style={thTd}>{row.viewed_count}</td>
                            <td style={thTd}>{row.purchased_count}</td>
                            {!isStaffScope ? <td style={thTd}>{row.is_active ? 'Yes' : 'No'}</td> : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!isStaffScope && rewardsRows.length > 0 ? (
                    <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
                      <button
                        type="button"
                        disabled={rewardsPage <= 1}
                        onClick={() => setRewardsPage(p => Math.max(1, p - 1))}
                        className="staff-page-button"
                        style={{ padding: '6px 14px', opacity: rewardsPage <= 1 ? 0.45 : 1 }}
                      >
                        <span className="staff-page-button-text">Previous</span>
                      </button>
                      <button
                        type="button"
                        disabled={rewardsPage >= rewardsTableTotalPages}
                        onClick={() => setRewardsPage(p => p + 1)}
                        className="staff-page-button"
                        style={{
                          padding: '6px 14px',
                          opacity: rewardsPage >= rewardsTableTotalPages ? 0.45 : 1,
                        }}
                      >
                        <span className="staff-page-button-text">Next</span>
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {rewardModalId ? (
            <div
              style={modalOverlay}
              role="presentation"
              onClick={e => {
                if (e.target === e.currentTarget) closeRewardModal();
              }}
            >
              <div style={modalBox} role="dialog" aria-modal="true" aria-labelledby="reward-analytics-title">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '20px 24px',
                    borderBottom: '1px solid #e9ecef',
                  }}
                >
                  <div>
                    <h2 id="reward-analytics-title" className="staff-page-header" style={{ fontSize: 20, margin: 0 }}>
                      {rewardDetail?.reward?.title || 'Reward'}
                    </h2>
                    <p className="staff-page-subheader" style={{ marginTop: 8, marginBottom: 0 }}>
                      Period: {periodLabel} · since{' '}
                      {rewardDetail?.since ? formatDateTime(rewardDetail.since) : '—'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeRewardModal}
                    className="staff-page-button"
                    style={{ padding: '6px 14px' }}
                  >
                    <span className="staff-page-button-text">Close</span>
                  </button>
                </div>

                <div style={{ padding: '16px 24px' }}>
                  {rewardDetailLoading ? (
                    <p className="staff-page-content">Loading…</p>
                  ) : rewardDetail?.reward ? (
                    <>
                      {(() => {
                        const r = rewardDetail.reward;
                        const list = (r.images || [])
                          .map(im => ({ ...im, href: getRewardImageUrl(im.url) }))
                          .filter(im => im.href);
                        const primaryOnly = getRewardImageUrl(r.primary_image_url);
                        const show =
                          list.length > 0
                            ? list
                            : primaryOnly
                              ? [{ id: 'primary', href: primaryOnly, alt_text: r.title || '' }]
                              : [];
                        if (show.length === 0) return null;
                        return (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 12,
                              marginBottom: 20,
                              alignItems: 'flex-start',
                            }}
                          >
                            {show.map(im => (
                              <img
                                key={im.id}
                                src={im.href}
                                alt={im.alt_text || r.title || ''}
                                style={{
                                  maxWidth: 280,
                                  maxHeight: 220,
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  borderRadius: 8,
                                  border: '1px solid #e9ecef',
                                  background: '#fafafa',
                                }}
                              />
                            ))}
                          </div>
                        );
                      })()}
                      <div
                        className="staff-page-content"
                        style={{
                          display: 'grid',
                          gap: 8,
                          marginBottom: 20,
                          fontSize: 14,
                        }}
                      >
                        <div>
                          <strong>Type</strong>: {rewardDetail.reward.reward_type} · <strong>Redemption</strong>:{' '}
                          {rewardDetail.reward.redemption_type}
                        </div>
                        <div>
                          <strong>Points</strong>: {rewardDetail.reward.points_cost}
                          {rewardDetail.reward.cash_price_cents != null ? (
                            <>
                              {' '}
                              · <strong>Card (cents)</strong>: {rewardDetail.reward.cash_price_cents}
                            </>
                          ) : null}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{rewardDetail.reward.description}</div>
                      </div>

                      <h3 className="staff-page-header" style={{ fontSize: 16, marginBottom: 8 }}>
                        Who viewed (distinct users)
                      </h3>
                      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thTd}>User</th>
                              <th style={thTd}>Views</th>
                              <th style={thTd}>Last view</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(rewardDetail.viewers?.results || []).map((v, i) => (
                              <tr key={`${v.user_id ?? 'anon'}-${i}`}>
                                <td style={thTd}>{v.user_email || v.label || '—'}</td>
                                <td style={thTd}>{v.view_count}</td>
                                <td style={thTd}>{formatDateTime(v.last_view)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
                          <button
                            type="button"
                            disabled={rewardViewersPage <= 1}
                            onClick={() => setRewardViewersPage(p => Math.max(1, p - 1))}
                            className="staff-page-button"
                            style={{ padding: '6px 14px', opacity: rewardViewersPage <= 1 ? 0.45 : 1 }}
                          >
                            <span className="staff-page-button-text">Prev</span>
                          </button>
                          <span className="staff-page-subheader">
                            Page {rewardDetail.viewers?.page ?? rewardViewersPage} /{' '}
                            {rewardDetail.viewers?.total_pages ?? 1}
                          </span>
                          <button
                            type="button"
                            disabled={rewardViewersPage >= (rewardDetail.viewers?.total_pages ?? 1)}
                            onClick={() => setRewardViewersPage(p => p + 1)}
                            className="staff-page-button"
                            style={{
                              padding: '6px 14px',
                              opacity:
                                rewardViewersPage >= (rewardDetail.viewers?.total_pages ?? 1) ? 0.45 : 1,
                            }}
                          >
                            <span className="staff-page-button-text">Next</span>
                          </button>
                        </div>
                      </div>

                      <h3 className="staff-page-header" style={{ fontSize: 16, marginBottom: 8 }}>
                        Redemptions
                      </h3>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thTd}>User</th>
                              <th style={thTd}>Requested</th>
                              <th style={thTd}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(rewardDetail.purchases?.results || []).map(pur => (
                              <tr key={pur.id}>
                                <td style={thTd}>{pur.user_email || '—'}</td>
                                <td style={thTd}>{formatRedemptionDateTime(pur.requested_at)}</td>
                                <td style={thTd}>{pur.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center' }}>
                          <button
                            type="button"
                            disabled={rewardPurchasesPage <= 1}
                            onClick={() => setRewardPurchasesPage(p => Math.max(1, p - 1))}
                            className="staff-page-button"
                            style={{ padding: '6px 14px', opacity: rewardPurchasesPage <= 1 ? 0.45 : 1 }}
                          >
                            <span className="staff-page-button-text">Prev</span>
                          </button>
                          <span className="staff-page-subheader">
                            Page {rewardDetail.purchases?.page ?? rewardPurchasesPage} /{' '}
                            {rewardDetail.purchases?.total_pages ?? 1}
                          </span>
                          <button
                            type="button"
                            disabled={
                              rewardPurchasesPage >= (rewardDetail.purchases?.total_pages ?? 1)
                            }
                            onClick={() => setRewardPurchasesPage(p => p + 1)}
                            className="staff-page-button"
                            style={{
                              padding: '6px 14px',
                              opacity:
                                rewardPurchasesPage >= (rewardDetail.purchases?.total_pages ?? 1)
                                  ? 0.45
                                  : 1,
                            }}
                          >
                            <span className="staff-page-button-text">Next</span>
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="staff-page-content">Could not load reward.</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {!isStaffScope ? (
          <div className="add-reward-section" style={{ marginTop: 24 }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div>
                <h2 className="staff-page-header" style={{ fontSize: '22px', marginBottom: '8px' }}>
                  Product events
                </h2>
                <p className="staff-page-subheader" style={{ opacity: 0.7 }}>
                  {eventsCount} in selected period · page {eventsPage} of {eventsTotalPages} ·{' '}
                  {eventsPageSize} per page
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 16,
                  justifyContent: 'flex-end',
                }}
              >
                <label
                  className="staff-page-subheader"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 1 }}
                >
                  Per page
                  <select
                    value={eventsPageSize}
                    onChange={e => setEventsPageSize(Number(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #e9ecef',
                      fontFamily: 'inherit',
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="staff-page-subheader" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Type
                  <select
                    value={eventTypeFilter}
                    onChange={e => setEventTypeFilter(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #e9ecef',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="">All</option>
                    <option value="screen_visit">screen_visit</option>
                    <option value="search">search</option>
                    <option value="offer_detail_view">offer_detail_view</option>
                    <option value="offer_link_click">offer_link_click</option>
                    <option value="reward_detail_view">reward_detail_view</option>
                    <option value="reward_click">reward_click</option>
                  </select>
                </label>
              </div>
            </div>
            {eventsLoading ? (
              <div
                style={{
                  minHeight: analyticTableSkeletonMinHeight(eventsPageSize),
                  marginTop: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <p className="staff-page-content">Loading events…</p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto', marginTop: 16 }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thTd}>When</th>
                        <th style={thTd}>Type</th>
                        <th style={{ ...thTd, width: 72 }}>Client</th>
                        <th style={thTd}>User</th>
                        <th style={thTd}>Payload</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map(row => (
                        <tr key={row.id}>
                          <td style={thTd}>{formatDateTime(row.created_at)}</td>
                          <td style={thTd}>{row.event_type}</td>
                          <td style={thTd}>{row.client_surface || '—'}</td>
                          <td style={thTd}>{row.user_email || (row.user_id ? `#${row.user_id}` : '—')}</td>
                          <td
                            style={{
                              ...thTd,
                              fontFamily: 'ui-monospace, monospace',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {formatPayloadFull(row.payload)}
                          </td>
                        </tr>
                      ))}
                      {eventFillerRows(Math.max(0, eventsPageSize - events.length))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={eventsPage <= 1}
                    onClick={() => setEventsPage(p => Math.max(1, p - 1))}
                    className="staff-page-button"
                    style={{ padding: '6px 14px', opacity: eventsPage <= 1 ? 0.45 : 1 }}
                  >
                    <span className="staff-page-button-text">Previous</span>
                  </button>
                  <button
                    type="button"
                    disabled={eventsPage >= eventsTotalPages}
                    onClick={() => setEventsPage(p => p + 1)}
                    className="staff-page-button"
                    style={{
                      padding: '6px 14px',
                      opacity: eventsPage >= eventsTotalPages ? 0.45 : 1,
                    }}
                  >
                    <span className="staff-page-button-text">Next</span>
                  </button>
                </div>
              </>
            )}
          </div>
          ) : null}

          {!isStaffScope ? (
          <div className="add-reward-section" style={{ marginTop: 24, marginBottom: 48 }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div>
                <h2 className="staff-page-header" style={{ fontSize: '22px', marginBottom: '8px' }}>
                  Successful logins
                </h2>
                <p className="staff-page-subheader" style={{ opacity: 0.7 }}>
                  {loginsCount} in selected period
                  {loginsClientSurface ? (
                    <>
                      {' '}
                      · client: <strong>{loginsClientSurface}</strong>
                    </>
                  ) : null}
                  {' · '}
                  page {loginsPage} of {loginsTotalPages} · {loginsPageSize} per page
                </p>
              </div>
              <div
                className="staff-page-subheader"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}
              >
                <label
                  className="staff-page-subheader"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 1 }}
                >
                  Per page
                  <select
                    value={loginsPageSize}
                    onChange={e => setLoginsPageSize(Number(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #e9ecef',
                      fontFamily: 'inherit',
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            {loginsLoading ? (
              <div
                style={{
                  minHeight: analyticTableSkeletonMinHeight(loginsPageSize),
                  marginTop: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <p className="staff-page-content">Loading…</p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto', marginTop: 16 }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thTd}>When</th>
                        <th style={thTd}>Email</th>
                        <th style={thTd}>Method</th>
                        <th style={{ ...thTd, width: 72 }}>Client</th>
                        <th style={thTd}>Platform</th>
                        <th style={thTd}>App</th>
                        <th style={thTd}>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logins.map(row => (
                        <tr key={row.id}>
                          <td style={thTd}>{formatDateTime(row.created_at)}</td>
                          <td style={thTd}>{row.user_email}</td>
                          <td style={thTd}>{row.method}</td>
                          <td style={thTd}>{row.client_surface || '—'}</td>
                          <td style={thTd}>{row.platform || '—'}</td>
                          <td style={{ ...thTd, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                            {[row.app_version, row.build_version].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td style={thTd}>{row.ip_address || '—'}</td>
                        </tr>
                      ))}
                      {loginFillerRows(Math.max(0, loginsPageSize - logins.length))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={loginsPage <= 1}
                    onClick={() => setLoginsPage(p => Math.max(1, p - 1))}
                    className="staff-page-button"
                    style={{ padding: '6px 14px', opacity: loginsPage <= 1 ? 0.45 : 1 }}
                  >
                    <span className="staff-page-button-text">Previous</span>
                  </button>
                  <button
                    type="button"
                    disabled={loginsPage >= loginsTotalPages}
                    onClick={() => setLoginsPage(p => p + 1)}
                    className="staff-page-button"
                    style={{
                      padding: '6px 14px',
                      opacity: loginsPage >= loginsTotalPages ? 0.45 : 1,
                    }}
                  >
                    <span className="staff-page-button-text">Next</span>
                  </button>
                </div>
              </>
            )}
          </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SuperuserAnalytics;
